import { MaterialsType } from "@/types";
import useGlobals from "@/utils/hooks/useGlobals";
import { uuid } from "@/utils/string.ts";
import Button from "@edusogno/primitives/button.js";
import LoadingButton from "@edusogno/primitives/loading-button.js";
import { ChevronDownIcon, X } from "lucide-react";
import { useEffect, useState } from "react";
import AltDownloadIcon from "../icons/CaDownloadIcon";
import MaterialsBadge from "./MaterialsBadge";

type Props = {
  url: string;
  type?: MaterialsType;
  title: string;
  subtype: string;
  fileType: string;
  onClose?: () => void;
  pdfDetails: { maxPages: number; currentPage: number };
  setPdfDetails: React.Dispatch<
    React.SetStateAction<{ maxPages: number; currentPage: number }>
  >;
};

const MaterialsLayoutHeader = ({
  url,
  type = MaterialsType.SPEAKING,
  title,
  subtype,
  onClose,
  fileType,
  pdfDetails,
  setPdfDetails,
}: Props) => {
  const { setGlobalState } = useGlobals();

  const [downloading, setDownloading] = useState(false);

  const downloadPDF = (downloadURL: string) => {
    setDownloading(true);
    void fetch(downloadURL)
      .then((res) => res.blob())
      .then((blob) => {
        const link = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = link;
        a.download = uuid() + (fileType == "video" ? ".mp4" : ".pdf");
        a.click();
        setDownloading(false);
      });
  };

  useEffect(() => {
    setGlobalState("showSideBar", false);
    return () => {
      setGlobalState("showSideBar", true);
    };
  }, [setGlobalState]);

  return (
    <div className="flex flex-col relative xl:flex-row items-start xl:items-center w-full justify-between gap-4">
      <div className="flex flex-row items-center xl:gap-4 justify-between gap-2 mt-6">
        <MaterialsBadge
          type={type as MaterialsType}
          subtype={subtype}
          className="text-white border-white"
        />

        <p className="xl:text-base truncate text-xs font-semibold text-white">
          {title}
        </p>
      </div>

      <div className="rounded-md bg-brand py-1 flex flex-row items-center justify-center h-fit gap-4 until-xl:justify-between until-xl:w-full">
        <div className="flex items-center justify-between gap-2">
          <Button
            disabled={pdfDetails.currentPage === 1}
            variant="outlined"
            color="white"
            className="!min-w-[25px] w-6 h-6"
            onClick={() => {
              setPdfDetails((prev) =>
                pdfDetails.currentPage - 1 <= 0
                  ? { ...prev, currentPage: prev.maxPages }
                  : { ...prev, currentPage: prev.currentPage - 1 },
              );
            }}
          >
            <ChevronDownIcon className="rotate-90 stroke-white w-4 h-auto" />
          </Button>

          <div className="border-solid border-2 text-lg text-brand bg-white  flex justify-center items-center text w-[111px] border-lightPrimary rounded-md h-[40px]">
            <p className="text-[10px] font-medium">
              Page: {`${pdfDetails.currentPage}/${pdfDetails.maxPages}`}
            </p>
          </div>

          <Button
            disabled={pdfDetails.currentPage === pdfDetails.maxPages}
            variant="outlined"
            color="white"
            className="!min-w-[25px] w-6 h-6"
            onClick={() => {
              setPdfDetails((r) =>
                pdfDetails.currentPage + 1 > r.maxPages
                  ? { ...r, currentPage: 1 }
                  : { ...r, currentPage: r.currentPage + 1 },
              );
            }}
          >
            <ChevronDownIcon className="-rotate-90 stroke-white w-4 h-auto" />
          </Button>
        </div>

        <LoadingButton
          size="sm"
          color="white"
          variant="contained"
          loading={downloading}
          onClick={() => downloadPDF(url)}
          disabled={downloading}
          className="!min-w-[40px] w-10 h-10"
        >
          <AltDownloadIcon className="w-6 h-6" />
        </LoadingButton>

        <Button
          color="white"
          variant="ghost"
          onClick={onClose}
          className="w-fit until-xl:h-[22px] until-xl:absolute until-xl:top-0 until-xl:right-0"
        >
          <X />
        </Button>
      </div>
    </div>
  );
};

export default MaterialsLayoutHeader;
